<script setup lang="ts">
import HeaderBar from '../components/HeaderBar.vue'
import specialImg from '@/assets/images/sales/sale-2.webp'

const imageSrc = specialImg;
</script>

<template>
  <HeaderBar />

  <main class="home-main">
    <h1 class="main-title">Protección Integral contra Incendios</h1>
    <p class="main-subtitle">Soluciones para empresas y hogares en México</p>

    <!-- HERO SECTION -->
    <section class="hero-section">
      <div class="hero-image-container">
        <img :src="imageSrc" class="hero-image" alt="Equipo de protección contra incendios" />
      </div>
      <div class="hero-content">
        <h2 class="hero-title">Expertos en Seguridad contra Incendios</h2>
        <p class="hero-text">
          Servicios certificados NOM-STPS y protección civil, liderados por un profesional con experiencia como bombero en EE.UU.
        </p>
        <a
          class="hero-cta"
          href="https://wa.me/528110381969"
          target="_blank"
          rel="noopener"
        >
          📲 Cotización Rápida por WhatsApp
        </a>
      </div>
    </section>

    <!-- FEATURES SECTION -->
    <section class="features-section">
      <h2 class="section-title">Nuestros Servicios</h2>
      <div class="features-grid">
        <div class="feature-card">
          <h3 class="feature-title">Extintores Certificados</h3>
          <p class="feature-desc">Venta, recarga y mantenimiento. Asesoría en normatividad.</p>
        </div>
        <div class="feature-card">
          <h3 class="feature-title">Señalización y Equipos</h3>
          <p class="feature-desc">
            Equipos certificados y señalización.
            <router-link class="equip-link" to="/sala-equipos">Ver sala de equipos</router-link>
          </p>
        </div>
        <div class="feature-card">
          <h3 class="feature-title">Atención Inmediata</h3>
          <p class="feature-desc">Asesoría bilingüe y soporte vía WhatsApp para empresas.</p>
        </div>
      </div>
    </section>

    <!-- FOOTER NOTE -->
    <footer class="homepage-footer">
      <p>Cumplimiento NOM y STPS. <strong>FireStop MX — Tu aliado en seguridad.</strong></p>
    </footer>
  </main>
</template>

<style scoped>
/* Use modern color palette */
:global(html), :global(body) {
  background: var(--bg-primary);
}

body {
  background: var(--bg-primary);
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
}

/* main background = modern neutral */
.home-main {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  min-height: calc(100vh - 80px);
  background: var(--bg-primary); /* Back to using CSS variable */
  display: flex;
  flex-direction: column;
  align-items: center;
}

.main-title {
  font-size: 2.5rem;
  color: var(--brand-red);
  text-align: center;
  margin-bottom: 0.5rem;
}

.main-subtitle {
  font-size: 1.25rem;
  color: var(--text-secondary);
  text-align: center;
  margin-bottom: 2rem;
}

/* HERO SECTION */
.hero-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 3rem;
  padding: 2rem;
  background: var(--bg-secondary);
  border-radius: var(--radius-lg);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2); /* Deeper shadow */
  width: 100%;
  max-width: 1000px;
}

.hero-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 600px;
}

.hero-title {
  font-size: 1.75rem;
  color: var(--brand-red);
  margin-bottom: 1rem;
  text-align: center;
}

.hero-text {
  font-size: 1.1rem;
  color: var(--text-primary);
  line-height: 1.6;
  margin-bottom: 1.5rem;
  text-align: center;
}

.hero-cta {
  display: inline-block;
  background: var(--brand-green);
  color: #fff;
  padding: 0.8rem 1.5rem;
  border-radius: var(--radius-sm);
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  transition: background 0.2s, transform 0.2s;
}

.hero-cta:hover {
  background: #1ebea5;
  transform: translateY(-2px);
}

.hero-image-container {
  width: 100%;
  max-width: 600px;
  display: flex;
  justify-content: center;
}

.hero-image {
  width: 100%;
  border-radius: var(--radius-sm);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.18); /* Deeper shadow */
}

/* FEATURES SECTION */
.features-section {
  margin-bottom: 3rem;
  width: 100%;
  max-width: 1000px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: transparent; /* transparent, so the parent neutral shows */
}

.section-title {
  font-size: 1.8rem;
  color: var(--brand-red);
  text-align: center;
  margin-bottom: 2rem;
}

.features-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  width: 100%;
  max-width: 800px;
  background: transparent; /* transparent, so the parent neutral shows */
}

.feature-card {
  background: var(--bg-secondary);
  padding: 1.5rem;
  border-radius: var(--radius-sm);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.18); /* Deeper shadow */
  transition: transform 0.2s, box-shadow 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.feature-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.22); /* Even deeper hover shadow */
}

.feature-title {
  font-size: 1.2rem;
  color: var(--brand-red);
  margin-bottom: 0.5rem;
  text-align: center;
}

.feature-desc {
  font-size: 1rem;
  color: var(--text-secondary);
  line-height: 1.5;
  text-align: center;
}

.equip-link {
  color: var(--brand-blue);
  text-decoration: underline;
  font-weight: 500;
}

.equip-link:hover {
  color: #156097;
}

/* FOOTER */
.homepage-footer {
  text-align: center;
  font-size: 0.95rem;
  color: var(--text-secondary);
  padding: 1.5rem 0;
  width: 100%;
  max-width: 1000px;
  background: var(--bg-primary); /* matches neutral, not white */
}

/* DESKTOP */
@media (min-width: 768px) {
  .main-title {
    font-size: 3rem;
  }

  .main-subtitle {
    font-size: 1.5rem;
  }

  .hero-section {
    flex-direction: row;
    gap: 3rem;
    padding: 3rem;
  }

  .hero-content {
    flex: 1;
  }

  .hero-title {
    font-size: 2rem;
  }

  .hero-image-container {
    flex: 1;
  }

  .features-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* MOBILE */
@media (max-width: 767px) {
  .home-main {
    padding: 1.5rem;
    max-width: 100%;
  }

  .main-title {
    font-size: 2rem;
  }

  .main-subtitle {
    font-size: 1.1rem;
  }

  .hero-section {
    padding: 1.5rem;
    width: 100%;
  }

  .hero-title {
    font-size: 1.5rem;
  }

  .hero-cta {
    width: 100%;
    text-align: center;
  }

  .feature-card {
    padding: 1rem;
  }
}
</style>
